import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Bell, Save, RotateCcw, AlertTriangle, HardDrive, Cpu, Activity, Server } from 'lucide-react';

const alertTypeIcons = {
  disk_space: HardDrive,
  cpu_usage: Cpu,
  ram_usage: Activity,
  service_stopped: Server
};

const SettingsTab = ({ agentId }) => {
  const [globalRules, setGlobalRules] = useState([]);
  const [assetRules, setAssetRules] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchRules();
  }, [agentId]);

  const fetchRules = async () => {
    try {
      const [globalRes, assetRes] = await Promise.all([
        api.get('/admin/alert-rules'),
        api.get(`/agents/${agentId}/alert-rules`)
      ]);
      
      setGlobalRules(globalRes.data || []);
      const assetRulesData = assetRes.data || [];
      setAssetRules(assetRulesData);
      
      // Build overrides map
      const overridesMap = {};
      assetRulesData.forEach(rule => {
        overridesMap[rule.global_rule_id] = {
          enabled: rule.enabled,
          threshold_value: rule.threshold_value,
          threshold_unit: rule.threshold_unit,
          severity: rule.severity,
          duration_minutes: rule.duration_minutes,
          service_name: rule.service_name
        };
      });
      setOverrides(overridesMap);
    } catch (error) {
      toast.error('Failed to load alert rules');
    } finally {
      setLoading(false);
    }
  };

  const toggleOverride = async (globalRule, enabled) => {
    if (enabled) {
      // Create asset-specific override
      setOverrides({
        ...overrides,
        [globalRule.id]: {
          enabled: true,
          threshold_value: globalRule.threshold_value,
          threshold_unit: globalRule.threshold_unit,
          severity: globalRule.severity,
          duration_minutes: globalRule.duration_minutes || 0,
          service_name: globalRule.service_name || ''
        }
      });
    } else {
      // Remove override (use global)
      const newOverrides = { ...overrides };
      delete newOverrides[globalRule.id];
      setOverrides(newOverrides);
    }
  };

  const updateOverride = (globalRuleId, field, value) => {
    setOverrides({
      ...overrides,
      [globalRuleId]: {
        ...overrides[globalRuleId],
        [field]: value
      }
    });
  };

  const saveOverrides = async () => {
    setSaving(true);
    try {
      await api.post(`/agents/${agentId}/alert-rules`, {
        overrides: overrides
      });
      toast.success('Alert settings saved');
      fetchRules();
    } catch (error) {
      toast.error('Failed to save alert settings');
    } finally {
      setSaving(false);
    }
  };

  const resetToGlobal = async (globalRuleId) => {
    const newOverrides = { ...overrides };
    delete newOverrides[globalRuleId];
    setOverrides(newOverrides);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'warning': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'info': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  if (loading) {
    return <div className="p-4 text-center">Loading alert settings...</div>;
  }

  return (
    <div className="p-4 space-y-4">
      <Tabs defaultValue="alerts" className="w-full">
        <TabsList className="grid w-full grid-cols-2 lg:w-[400px]">
          <TabsTrigger value="alerts">Alerts</TabsTrigger>
          <TabsTrigger value="other" disabled>Other Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="alerts" className="space-y-4 mt-4">
          {/* Header */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Bell className="w-5 h-5" />
                Asset-Specific Alert Rules
              </CardTitle>
              <p className="text-sm text-slate-600 mt-2">
                Override global alert rules for this asset. When disabled, global rules apply.
              </p>
            </CardHeader>
          </Card>

          {/* Alert Rules List */}
          {globalRules.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <AlertTriangle className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-600">No global alert rules configured</p>
                <p className="text-sm text-slate-500 mt-1">
                  Create global rules in Settings → Asset Alerts
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {globalRules.filter(r => r.enabled).map(rule => {
                const isOverridden = !!overrides[rule.id];
                const override = overrides[rule.id] || {};
                const Icon = alertTypeIcons[rule.alert_type] || AlertTriangle;

                return (
                  <Card key={rule.id} className={isOverridden ? 'border-blue-300 shadow-sm' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        {/* Rule Info */}
                        <div className="flex items-start gap-3 flex-1">
                          <div className="p-2 bg-slate-100 rounded-lg">
                            <Icon className="w-5 h-5 text-slate-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-slate-800">{rule.name}</h4>
                              {!isOverridden && (
                                <Badge variant="outline" className="text-xs">
                                  Using Global
                                </Badge>
                              )}
                              {isOverridden && (
                                <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs">
                                  Overridden
                                </Badge>
                              )}
                            </div>

                            {/* Show global values when not overridden */}
                            {!isOverridden && (
                              <div className="text-sm text-slate-600 space-y-1">
                                <p>
                                  Threshold: <strong>{rule.threshold_value}{rule.threshold_unit}</strong>
                                </p>
                                <Badge className={getSeverityColor(rule.severity)}>
                                  {rule.severity}
                                </Badge>
                              </div>
                            )}

                            {/* Show override form when overridden */}
                            {isOverridden && (
                              <div className="mt-3 space-y-3 bg-blue-50 p-3 rounded-lg">
                                {rule.alert_type !== 'service_stopped' ? (
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <Label className="text-xs">Threshold Value</Label>
                                      <Input
                                        type="number"
                                        value={override.threshold_value}
                                        onChange={(e) => updateOverride(rule.id, 'threshold_value', e.target.value)}
                                        className="h-8 text-sm"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Unit</Label>
                                      <Select
                                        value={override.threshold_unit}
                                        onValueChange={(value) => updateOverride(rule.id, 'threshold_unit', value)}
                                      >
                                        <SelectTrigger className="h-8 text-sm">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {rule.alert_type === 'disk_space' ? (
                                            <>
                                              <SelectItem value="%">%</SelectItem>
                                              <SelectItem value="GB">GB</SelectItem>
                                            </>
                                          ) : (
                                            <SelectItem value="%">%</SelectItem>
                                          )}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <Label className="text-xs">Service Name</Label>
                                    <Input
                                      value={override.service_name}
                                      onChange={(e) => updateOverride(rule.id, 'service_name', e.target.value)}
                                      className="h-8 text-sm"
                                    />
                                  </div>
                                )}

                                <div>
                                  <Label className="text-xs">Severity</Label>
                                  <Select
                                    value={override.severity}
                                    onValueChange={(value) => updateOverride(rule.id, 'severity', value)}
                                  >
                                    <SelectTrigger className="h-8 text-sm">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="info">Info</SelectItem>
                                      <SelectItem value="warning">Warning</SelectItem>
                                      <SelectItem value="critical">Critical</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>

                                {(rule.alert_type === 'cpu_usage' || rule.alert_type === 'ram_usage') && (
                                  <div>
                                    <Label className="text-xs">Duration (minutes)</Label>
                                    <Input
                                      type="number"
                                      value={override.duration_minutes}
                                      onChange={(e) => updateOverride(rule.id, 'duration_minutes', parseInt(e.target.value) || 0)}
                                      className="h-8 text-sm"
                                    />
                                  </div>
                                )}

                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => resetToGlobal(rule.id)}
                                  className="w-full text-xs"
                                >
                                  <RotateCcw className="w-3 h-3 mr-1" />
                                  Reset to Global
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Override Toggle */}
                        <div className="flex flex-col items-end gap-2">
                          <div className="text-xs text-slate-500">Override</div>
                          <Switch
                            checked={isOverridden}
                            onCheckedChange={(checked) => toggleOverride(rule, checked)}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Save Button */}
          {globalRules.filter(r => r.enabled).length > 0 && (
            <div className="flex justify-end">
              <Button onClick={saveOverrides} disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Alert Settings'}
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="other">
          <Card>
            <CardContent className="py-8 text-center text-slate-500">
              Additional asset settings coming soon...
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsTab;
